import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getDashboardChartFiltered } from 'api/dashboard';
import { getJSONObject } from 'utils/helper';
import { normalizePlotConfig } from 'utils/chat';
import dayjs from 'dayjs';
import ChartMeta from './widgets/Meta/ChartMeta';
import { getValueByPath, formatValue } from './widgets/Meta/metaUtils';
import { getStatComponent, getIcon } from './widgets/registry';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInitialDataThunk } from 'slices/initialData/thunk';
import SectionFilters from './Filters/SectionFilters';

// Fallback for unknown stat types
const UnknownStat = () => {
    return (
        <div className="py-6 text-sm text-gray-500">Unsupported stat type</div>
    );
};

// stat components and icons are resolved via registry to allow extension

export default function StatsRenderer({ section }) {
    const safeSection = section || {};
    const { type, title, subtitle = '', props = {}, initial, theme: sectionTheme = {} } = safeSection;
    const StatComp = getStatComponent(type) || UnknownStat;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [plotConfig, setPlotConfig] = useState(null);
    const [apiRow, setApiRow] = useState(null);

    const [isFilterable, setIsFilterable] = useState(false);
    const [range, setRange] = useState([null, null]);
    const [startDate, endDate] = range;
    const [hasFilteredOnce, setHasFilteredOnce] = useState(false);
    const [filters, setFilters] = useState({});

    // Initial load from centralized cache
    const dispatch = useDispatch();
    const key = initial?.dashboard_id && initial?.chartid ? `${initial.dashboard_id}:${initial.chartid}` : null;
    const cached = useSelector(state => key ? state.InitialDataReducer?.byKey?.[key] : null);
    const cachedLoading = useSelector(state => key ? state.InitialDataReducer?.loadingByKey?.[key] : false);
    const cachedError = useSelector(state => key ? state.InitialDataReducer?.errorByKey?.[key] : null);

    useEffect(() => {
        if (!key) {
            setLoading(false);
            return;
        }
        if (!cached && !cachedLoading) {
            dispatch(fetchInitialDataThunk({ dashboard_id: initial.dashboard_id, chartid: initial.chartid }));
        }
        setLoading(Boolean(cachedLoading));
        if (cached) {
            setRows(Array.isArray(cached.rows) ? cached.rows : []);
            setPlotConfig(cached.plotConfig || null);
            setApiRow(cached.apiRow || null);
            setIsFilterable(Boolean(cached.isFilterable));
        }
        if (cachedError) {
            setError('Failed to load stat');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, cached, cachedLoading, cachedError]);

    // Filtered load
    useEffect(() => {
        const shouldFilter = Boolean(isFilterable && startDate && endDate && initial?.chartid && initial?.dashboard_id);
        if (!shouldFilter) return;
        let isMounted = true;
        const fetchFiltered = async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await getDashboardChartFiltered({
                    chartid: initial.chartid,
                    dashboard_id: initial.dashboard_id,
                    start_date: dayjs(startDate).format('YYYY-MM-DD'),
                    end_date: dayjs(endDate).format('YYYY-MM-DD'),
                });
                const r = Array.isArray(resp?.[0]) ? resp[0] : [];
                const row = r?.[0] ?? null;
                if (!isMounted) return;
                const df = getJSONObject(row?.updated_df_data ? row.updated_df_data : row?.df_data) || [];
                const hc = getJSONObject(row?.highchart_code) || {};
                setRows(Array.isArray(df) ? df : []);
                setPlotConfig(hc);
                setApiRow(row || null);
                setHasFilteredOnce(true);
            } catch (err) {
                if (!isMounted) return;
                setError('Failed to apply filter');
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchFiltered();
        return () => { isMounted = false; };
    }, [isFilterable, startDate, endDate, initial?.chartid, initial?.dashboard_id]);

    // Reload initial when filter cleared using cached initial (no refetch)
    useEffect(() => {
        const shouldReloadInitial = Boolean(
            isFilterable && hasFilteredOnce && !startDate && !endDate && initial?.chartid && initial?.dashboard_id
        );
        if (!shouldReloadInitial) return;
        if (cached) {
            setRows(Array.isArray(cached.rows) ? cached.rows : []);
            setPlotConfig(cached.plotConfig || null);
            setApiRow(cached.apiRow || null);
            setHasFilteredOnce(false);
            setLoading(false);
        } else if (!cachedLoading && key) {
            dispatch(fetchInitialDataThunk({ dashboard_id: initial.dashboard_id, chartid: initial.chartid }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterable, hasFilteredOnce, startDate, endDate, key, cached, cachedLoading]);

    // Normalize plot config to infer x/y keys (same utility as charts)
    const normalized = useMemo(() => normalizePlotConfig(plotConfig, rows, title || 'Report', 'column'), [plotConfig, rows, title]);
    // Allow theme overrides for axis keys
    const effectiveXKey = sectionTheme?.xAxisKey || normalized.xAxisKey;
    const effectiveYKeys = useMemo(() => {
        if (Array.isArray(sectionTheme?.yAxisKeys) && sectionTheme.yAxisKeys.length > 0) return sectionTheme.yAxisKeys;
        if (sectionTheme?.yAxisKey) return [sectionTheme.yAxisKey];
        return Array.isArray(normalized.yAxisKeys) ? normalized.yAxisKeys : [];
    }, [sectionTheme?.yAxisKeys, sectionTheme?.yAxisKey, normalized.yAxisKeys]);
    const xKey = effectiveXKey;
    const yKey = Array.isArray(effectiveYKeys) ? effectiveYKeys[0] : undefined;

    // Helpers to resolve per-row values for list-based stat cards
    const resolveFromRow = useCallback((row, spec) => {
        if (!spec) return undefined;
        const { path, format: fmt, prefix, suffix, maximumFractionDigits, default: def, variables, display } = spec;

        // Template variables support: { variables: { m: { path: 'male_count', default: 0 }, f: { path: 'female_count', default: 0 } }, display: "${m} M, ${f} F" }
        if (variables && typeof display === 'string') {
            const getByPath = (p) => {
                if (!p || typeof p !== 'string') return undefined;
                const normalizedPath = p.startsWith('data.') ? p.slice(5)
                    : (p.startsWith('row.') ? p.slice(4) : p);
                const parts = normalizedPath.split('.').filter(Boolean);
                let cur = row;
                for (let i = 0; i < parts.length && cur != null; i++) cur = cur?.[parts[i]];
                return cur;
            };
            const resolvedVars = Object.entries(variables).reduce((acc, [key, defn]) => {
                if (defn && typeof defn === 'object' && (typeof defn.path === 'string' || defn.hasOwnProperty('default'))) {
                    const val = getByPath(defn.path);
                    acc[key] = (val ?? defn.default ?? '');
                } else if (typeof defn === 'string') {
                    acc[key] = (getByPath(defn) ?? '');
                } else {
                    acc[key] = defn;
                }
                return acc;
            }, {});
            const out = display.replace(/\$\{(\w+)\}/g, (_m, k) => String(resolvedVars[k] ?? ''));
            return formatValue(out, fmt || 'string', { prefix: prefix || '', suffix: suffix || '', maximumFractionDigits });
        }

        // Path-based value resolution
        let raw;
        if (path === '$x') {
            raw = xKey ? row?.[xKey] : undefined;
        } else if (path === '$y') {
            raw = yKey ? row?.[yKey] : undefined;
        } else if (typeof path === 'string' && path.length > 0) {
            // Support simple dot paths relative to the row
            // Also accept explicit roots like 'data.' or 'row.' to improve readability in layout config
            const normalizedPath = path.startsWith('data.') ? path.slice(5)
                : (path.startsWith('row.') ? path.slice(4) : path);
            const parts = normalizedPath.split('.').filter(Boolean);
            let cur = row;
            for (let i = 0; i < parts.length && cur != null; i++) cur = cur?.[parts[i]];
            raw = cur;
        }
        return formatValue(raw ?? def, fmt || 'string', { prefix: prefix || '', suffix: suffix || '', maximumFractionDigits });
    }, [xKey, yKey]);

    const percentFromRow = useCallback((row, spec, maxVal) => {
        if (!spec) return undefined;
        if (spec.path === '$y') {
            const v = Number(yKey ? row?.[yKey] : undefined);
            if (Number.isFinite(v) && Number.isFinite(maxVal) && maxVal > 0) return (v / maxVal) * 100;
        }
        const n = Number(resolveFromRow(row, { ...spec, format: undefined }));
        return Number.isFinite(n) ? n : undefined;
    }, [resolveFromRow, yKey]);

    const items = useMemo(() => {
        if (!Array.isArray(rows) || !xKey || !yKey) return [];
        const listCfg = (sectionTheme?.list || props?.list) || {};
        const itemSpec = listCfg.itemSpec || null;
        if (!itemSpec) {
            return rows.map((r) => ({ label: r?.[xKey], value: r?.[yKey] }));
        }
        const max = rows.reduce((m, r) => {
            const v = Number(yKey ? r?.[yKey] : undefined);
            return Number.isFinite(v) ? Math.max(m, v) : m;
        }, 0);
        return rows.map((r) => ({
            label: resolveFromRow(r, itemSpec.label ?? { path: '$x' }),
            subLabel: resolveFromRow(r, itemSpec.subLabel),
            usersText: resolveFromRow(r, itemSpec.users ?? { path: '$y', suffix: ' users' }),
            splitText: resolveFromRow(r, itemSpec.split),
            percentValue: percentFromRow(r, itemSpec.percent ?? { path: '$y' }, max),
        }));
    }, [rows, xKey, yKey, sectionTheme?.list, props?.list, resolveFromRow, percentFromRow]);

    // Resolve dynamic stat title (optional)
    const dataContext = useMemo(() => ({ apiRow, rows, normalized, plotConfig, props }), [apiRow, rows, normalized, plotConfig]);
    const statTitleSpec = props?.statTitle || safeSection?.statTitle;
    const resolvedStatTitle = useMemo(() => {
        if (!statTitleSpec) return null;
        if (typeof statTitleSpec === 'string') return { text: statTitleSpec };
        const raw = statTitleSpec.path ? getValueByPath(dataContext, statTitleSpec.path) : undefined;
        const text = formatValue(
            raw ?? statTitleSpec.default ?? '',
            statTitleSpec.format || 'string',
            {
                prefix: statTitleSpec.prefix || '',
                suffix: statTitleSpec.suffix || '',
                maximumFractionDigits: statTitleSpec.maximumFractionDigits,
            }
        );
        const iconName = statTitleSpec.icon;
        let iconSrc = getIcon('user');
        if (iconName && getIcon(iconName)) iconSrc = getIcon(iconName);
        if (iconName === "none") iconSrc = null;
        return {
            text,
            iconSrc,
            color: statTitleSpec.color,
            size: statTitleSpec.size,
            fontWeight: statTitleSpec.fontWeight,
        };
    }, [statTitleSpec, dataContext]);

    // Typography mapping for stat title size (extensible)
    const STAT_TITLE_SIZE_MAP = {
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg',
    };

    const handleFilterUpdate = useCallback((selectedFilter, filterOption) => {
        if (filterOption?.type === "basic" && filterOption?.param?.[0]) {
            setFilters(prev => ({
                ...prev,
                [filterOption?.param[0]]: selectedFilter?.value,
            }));
        }
    }, []);

    return (
        <div className="dashboard-new-card">
            <div className="flex items-center justify-between">
                {resolvedStatTitle ? (
                    <div className="flex items-center gap-3">
                        {resolvedStatTitle.iconSrc ? (
                            <img src={resolvedStatTitle.iconSrc} alt="" className="h-7 w-7" />
                        ) : null}
                        <h3 className={`${STAT_TITLE_SIZE_MAP[resolvedStatTitle.size] || 'text-base'} ${resolvedStatTitle?.fontWeight || 'font-medium'}  ${resolvedStatTitle.color || 'text-[#641C1A]'}`}>
                            {resolvedStatTitle.text}
                        </h3>
                    </div>
                ) : (
                    <div className="flex flex-col justify-start gap-1">
                        <h3 className={`text-base font-medium`}>{title}</h3>
                        <p className={`text-sm font-normal`}>{subtitle}</p>
                    </div>
                )}

                {/* {isFilterable || safeSection?.filters?.show ? (
                    <div>
                        <CustomDatePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => setRange(update)}
                            dateFormat="MM/yy"
                            placeholderText="Date Range"
                            showPopperArrow={false}
                        />
                    </div>
                ) : null} */}

                <SectionFilters
                    filtersConfig={safeSection?.filters}
                    isFilterable={isFilterable}
                    startDate={startDate}
                    endDate={endDate}
                    onDateRangeChange={setRange}
                    onBasicFilterChange={handleFilterUpdate}
                    variant="header"
                />
            </div>

            {error ? (
                <div className="mt-2 text-sm" style={{ color: '#ef4444' }}>{error}</div>
            ) : null}

            {(props?.meta || safeSection?.meta) ? (
                <ChartMeta
                    meta={props?.meta || safeSection?.meta}
                    dataContext={{ apiRow, rows, normalized, plotConfig }}
                    chartType={type}
                    theme={sectionTheme}
                    rightExtras={
                        <SectionFilters
                            filtersConfig={safeSection?.filters}
                            isFilterable={isFilterable}
                            startDate={startDate}
                            endDate={endDate}
                            onDateRangeChange={setRange}
                            onBasicFilterChange={handleFilterUpdate}
                            variant="meta"
                        />
                    }
                />
            ) : null}

            <div className="relative mt-2" aria-busy={loading ? 'true' : 'false'}>
                <StatComp
                    // Provide section props for cards that rely on explicit values
                    {...props}
                    dataContext={dataContext}
                    items={items}
                    rows={rows}
                    xKey={xKey}
                    yAxisKeys={Array.isArray(effectiveYKeys) ? effectiveYKeys : []}
                    theme={sectionTheme}
                />

                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <svg className="h-6 w-6 animate-spin text-gray-600" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                    </div>
                ) : null}
            </div>
        </div>
    );
}


